import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  cardArt,
  daysText,
  lastDay,
  money,
  periodName,
  quarterName,
  shortDay,
  urgency,
  type Period,
  type PerkItem,
} from '@/lib/perks'

const URGENCY_TEXT = {
  urgent: 'text-danger',
  soon: 'text-warning',
  calm: 'text-gray-500 dark:text-gray-400',
} as const

const URGENCY_DOT = {
  urgent: 'bg-danger',
  soon: 'bg-warning',
  calm: 'bg-gray-400 dark:bg-gray-500',
} as const

/** A credit card face; `mini` is the small chip used in rows. */
export function CardArt({
  art,
  name,
  issuer,
  mask,
  mini = false,
  className,
}: {
  art: string
  name?: string
  issuer?: string
  mask?: string | null
  mini?: boolean
  className?: string
}) {
  const style = cardArt(art)
  const face = {
    backgroundImage: `linear-gradient(115deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0) 62%), linear-gradient(135deg, ${style.from}, ${style.to})`,
  }
  if (mini) {
    return (
      <div
        aria-hidden
        className={cn('h-[22px] w-[34px] shrink-0 rounded-[4px] ring-1 ring-black/10', className)}
        style={face}
      />
    )
  }
  const text = style.light ? 'text-gray-900' : 'text-white'
  return (
    <div
      role="img"
      aria-label={[issuer, name, mask && `ending ${mask}`].filter(Boolean).join(', ')}
      className={cn('relative flex aspect-[1.586] w-full flex-col justify-between rounded-2xl p-6 shadow-lg', text, className)}
      style={face}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] opacity-70">{issuer}</div>
      <div className="flex items-end justify-between gap-4">
        <div className="text-2xl font-semibold leading-tight">{name}</div>
        {mask && <div className="shrink-0 font-medium tabular-nums opacity-80">•• {mask}</div>}
      </div>
    </div>
  )
}

/**
 * The quarter as a line: elapsed part filled, today as a notch, a dot on each
 * unused credit's last day coloured by how close it is.
 */
export function QuarterRail({ quarter, today, items }: { quarter: Period; today: Date; items: PerkItem[] }) {
  const span = quarter.end.getTime() - quarter.start.getTime()
  const pct = (d: Date) => Math.min(Math.max((d.getTime() - quarter.start.getTime()) / span, 0), 1) * 100
  const months = [0, 1, 2].map((i) => new Date(quarter.start.getFullYear(), quarter.start.getMonth() + i, 1))
  // One dot per day; the most urgent colour wins.
  const dots = new Map<string, PerkItem>()
  for (const item of items) {
    const key = lastDay(item.status.period).toDateString()
    const existing = dots.get(key)
    if (!existing || item.daysLeft < existing.daysLeft) dots.set(key, item)
  }
  return (
    <div
      className="relative h-12"
      role="img"
      aria-label={`${quarterName(quarter)}: ${items.length} credits due this quarter`}
    >
      <div className="absolute inset-x-1 top-3 h-[3px] rounded-full bg-gray-200 dark:bg-gray-800" />
      <div className="absolute left-1 top-3 h-[3px] rounded-full bg-gray-900 dark:bg-gray-100" style={{ width: `calc(${pct(today)}% - 0.25rem)` }} />
      {months.map((m, i) => (
        <div key={i} className="absolute top-7 text-[10px] font-semibold uppercase tracking-wider text-gray-400" style={{ left: `calc(${pct(m)}% + ${i === 0 ? 4 : 6}px)` }}>
          {m.toLocaleDateString('en-US', { month: 'short' })}
        </div>
      ))}
      {months.slice(1).map((m, i) => (
        <div key={`b${i}`} className="absolute top-3 h-[3px] w-[2px] bg-white dark:bg-gray-900" style={{ left: `${pct(m)}%` }} />
      ))}
      {[...dots.values()].map((item) => (
        <div
          key={item.benefit.id}
          title={`${item.benefit.name} · ${shortDay(lastDay(item.status.period))}`}
          className={cn('absolute top-[7.5px] h-3 w-3 -translate-x-1/2 rounded-full ring-2 ring-white dark:ring-gray-900', URGENCY_DOT[urgency(item.daysLeft)])}
          style={{ left: `${pct(lastDay(item.status.period))}%` }}
        />
      ))}
      <div className="absolute top-1 h-[17px] w-[3px] -translate-x-1/2 rounded-full bg-gray-900 dark:bg-gray-100" style={{ left: `${pct(today)}%` }} />
    </div>
  )
}

/** Benefit row: card chip, name and card, remaining value and time left. */
export function BenefitRow({
  item,
  showCard = true,
  onOpen,
  onQuickUse,
}: {
  item: PerkItem
  showCard?: boolean
  onOpen: () => void
  onQuickUse?: () => void
}) {
  const { benefit, status } = item
  const open = !status.resolved
  const certificate = benefit.kind === 'certificate'
  const parts: string[] = []
  if (showCard) parts.push(item.card.name)
  if (open) parts.push(`by ${shortDay(lastDay(status.period))}`)
  else parts.push(`${status.skipped ? 'Skipped' : 'Used'} ${periodName(benefit.cadence, benefit.basis)}`)
  if (item.creditLooksMissing) parts.push('credit missing')
  else if (item.waitingOn) parts.push('credit pending')
  if (benefit.requires_enrollment && !benefit.is_enrolled) parts.push('enroll')

  return (
    <div className="group flex items-center gap-1">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-4 rounded-lg px-3 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60"
      >
        <CardArt art={item.card.art} mini />
        <div className="min-w-0 flex-1">
          <div className={cn('truncate font-medium', open ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500')}>{benefit.name}</div>
          <div className={cn('truncate text-sm', item.creditLooksMissing ? 'text-danger' : 'text-gray-500 dark:text-gray-400')}>{parts.join(' · ')}</div>
          {open && !certificate && status.usedCents > 0 && (
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
              <div className="h-full rounded-full bg-success" style={{ width: `${status.fractionUsed * 100}%` }} />
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          {open ? (
            <>
              <div className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                {certificate ? 'Unused' : money(status.remainingCents)}
              </div>
              <div className={cn('text-sm font-medium tabular-nums', URGENCY_TEXT[urgency(item.daysLeft)])}>{daysText(item.daysLeft)}</div>
            </>
          ) : (
            <>
              <div className="tabular-nums text-gray-400">{status.skipped ? '—' : certificate ? 'Used' : money(status.usedCents)}</div>
              <Check className="ml-auto h-4 w-4 text-success" aria-label="Done" />
            </>
          )}
        </div>
      </button>
      {onQuickUse && (
        <button
          type="button"
          onClick={onQuickUse}
          disabled={!open}
          title={certificate ? 'Mark as used' : `Used all ${money(status.remainingCents)}`}
          aria-label={`Mark ${benefit.name} as used`}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-success-light hover:text-success focus-visible:opacity-100 disabled:invisible group-hover:opacity-100"
        >
          <Check className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

export function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400', className)}>{children}</div>
}
